import { ShieldAlert } from "lucide-react";
import { useState } from "react";
import { post } from "../lib/api";
import { useAction } from "../lib/hooks";
import type { MissionDetail } from "../lib/types";
import { Button } from "./ui/button";
import { Chip, Dialog, Field, H2, Panel, Textarea } from "./ui/primitives";

const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const TIER_HELP = { N1: "Código de producción no previsto: autoriza una persona", N2: "Ruta sensible (CI, infraestructura, secretos, dependencias, auth…): autorizan dos personas distintas" } as const;

/**
 * Alcance de la misión. Dos situaciones: (1) el plan no trae ficheros previstos → una persona define el alcance;
 * (2) la misión se bloqueó porque el diff toca ficheros fuera del plan → autorizar o pedir que se reviertan.
 */
export function ScopeCard({ d }: { d: MissionDetail }) {
  const [dlg, setDlg] = useState<"define" | "revert" | null>(null);
  const [files, setFiles] = useState("");
  const [criteria, setCriteria] = useState("");
  const [comment, setComment] = useState("");
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const id = d.mission.id;
  const close = () => { setDlg(null); setComment(""); };
  const define = useAction((b: { plannedFiles: string[]; acceptanceCriteria: string[] }) => post(`/missions/${id}/plan-scope`, b), { ok: "Alcance definido: revisa y aprueba el plan nuevo.", onDone: close });
  const decide = useAction((b: { decision: "approved" | "changes_requested"; files: string[]; comment?: string }) => post<{ resumed: boolean; remaining: number }>(`/missions/${id}/scope-decision`, b),
    { ok: (r) => (r.resumed ? "Alcance autorizado: la misión continúa." : "Decisión registrada."), onDone: close });

  const showDefine = d.actions.defineScope && !d.scope.verifiable;
  const showDecide = d.actions.decideScope && d.scope.pending.length > 0;
  if (!showDefine && !showDecide) return null;
  const chosen = d.scope.pending.filter((f) => picked[f.path] ?? true).map((f) => f.path);

  return (
    <Panel>
      <div className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-wn" /><H2>{showDefine ? "El plan no trae un alcance verificable" : "Ficheros fuera del plan aprobado"}</H2></div>
      {showDefine && (
        <>
          <p className="m-0 mt-2 max-w-[70ch] text-[14px] text-mu">Sin ficheros previstos la fábrica no puede comprobar que el agente se queda dentro de lo aprobado, así que no se autoriza el desarrollo. Define los ficheros y los criterios de aceptación; se creará una versión nueva del plan que tendrás que aprobar.</p>
          <div className="mt-3"><Button variant="primary" onClick={() => setDlg("define")}>Definir alcance</Button></div>
        </>
      )}
      {showDecide && (
        <>
          <p className="m-0 mt-2 max-w-[70ch] text-[14px] text-mu">La PR contiene ficheros que el plan no preveía. La misión no avanza (ni se autocorrige) hasta que se autoricen o se reviertan. Una autorización cubre solo esos ficheros.</p>
          <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
            {d.scope.pending.map((f) => (
              <li key={f.path} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-ln px-3 py-2 text-[14px]">
                <input type="checkbox" aria-label={`Seleccionar ${f.path}`} checked={picked[f.path] ?? true} onChange={(e) => setPicked({ ...picked, [f.path]: e.target.checked })} />
                <span className="font-mono">{f.path}</span>
                <Chip tone={f.tier === "N2" ? "bad" : "warn"} title={TIER_HELP[f.tier as "N1" | "N2"]}>{f.tier}</Chip>
                <span className="text-mu">{f.reason}</span>
                <span className="ml-auto font-mono text-[12px] text-mu">firmas {f.have}/{f.need}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="primary" disabled={!chosen.length} loading={decide.isPending} onClick={() => decide.mutate({ decision: "approved", files: chosen })}>Autorizar seleccionados</Button>
            <Button disabled={!chosen.length} onClick={() => setDlg("revert")}>Pedir que se reviertan</Button>
          </div>
        </>
      )}

      <Dialog open={dlg === "define"} onClose={close} title="Definir alcance del plan">
        <Field label="Ficheros previstos (uno por línea, rutas relativas al repositorio)"><Textarea rows={5} value={files} onChange={(e) => setFiles(e.target.value)} placeholder={"src/export/csv.ts\ntests/export.test.ts"} autoFocus /></Field>
        <Field label="Criterios de aceptación (uno por línea)"><Textarea rows={4} value={criteria} onChange={(e) => setCriteria(e.target.value)} /></Field>
        <div className="flex justify-end gap-2"><Button onClick={close}>Cancelar</Button><Button variant="primary" disabled={!lines(files).length || !lines(criteria).length} loading={define.isPending} onClick={() => define.mutate({ plannedFiles: lines(files), acceptanceCriteria: lines(criteria) })}>Crear plan nuevo</Button></div>
      </Dialog>
      <Dialog open={dlg === "revert"} onClose={close} title="Pedir que se reviertan los ficheros">
        <p className="m-0 text-[14px] text-mu">El agente recibirá la instrucción de revertir: <span className="font-mono">{chosen.join(", ")}</span>.</p>
        <Field label="Indicación para el agente"><Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} autoFocus /></Field>
        <div className="flex justify-end gap-2"><Button onClick={close}>Cancelar</Button><Button variant="primary" disabled={comment.trim().length < 3} loading={decide.isPending} onClick={() => decide.mutate({ decision: "changes_requested", files: chosen, comment })}>Enviar al agente</Button></div>
      </Dialog>
    </Panel>
  );
}
